import { inject, Injectable, InjectionToken } from '@angular/core';

import { SupabaseService } from '../auth/supabase.service';
import type { AdminClinicStatistics, StatisticsPeriod } from './clinic-statistics.types';

/**
 * Translation keys for the words that name the page's scope ("every clinic",
 * "the network"). The Statistics page also serves the clinic-owner Admin
 * portal, where the same view covers only the owner's own clinics; those
 * routes provide owner wording here alongside an owner-scoped
 * ClinicStatisticsService.
 */
export interface StatisticsScopeText {
  readonly title: string;
  readonly subtitle: string;
  readonly kpiTitle: string;
  readonly compareTitle: string;
  readonly median: string;
}

export const STATISTICS_SCOPE_TEXT = new InjectionToken<StatisticsScopeText>('STATISTICS_SCOPE_TEXT', {
  providedIn: 'root',
  factory: () => ({
    title: 'adminStats.title',
    subtitle: 'adminStats.subtitle',
    kpiTitle: 'adminStats.kpi.title',
    compareTitle: 'adminStats.detail.compareTitle',
    median: 'adminStats.detail.median',
  }),
});

/**
 * Reads network-wide clinic health for Admin › Statistics.
 *
 * Bare `.rpc()` (public schema) like SystemHealthService: the facts live in
 * `crm.stats_clinic_daily`, which `authenticated` cannot read; the RPC
 * re-checks platform-admin / superAdmin itself.
 */
@Injectable({ providedIn: 'root' })
export class ClinicStatisticsService {
  private readonly supabase = inject(SupabaseService).client;

  async getStatistics(days: StatisticsPeriod): Promise<AdminClinicStatistics> {
    const { data, error } = await this.supabase.rpc('get_admin_clinic_statistics', {
      p_days: days,
    });

    if (error) {
      throw error;
    }

    return data as AdminClinicStatistics;
  }
}
